import {
  BREADCRUMBS_POSITION_OPTIONS,
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  IMAGE_ASPECT_RATIO_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks/useModuleStyles';
import { BaseModuleProps } from '@/common/types';
import { HeadingBlock, HeadingBlockProps } from '@/components/blocks/HeadingBlock';
import { ImageBlock, ImageBlockProps } from '@/components/blocks/ImageBlock';
import { Breadcrumbs, BreadcrumbsProps } from '@/components/Breadcrumbs/Breadcrumbs';
import classNames from 'classnames';
import React from 'react';
import styles from './HeroCTA.module.scss';
import { getHeroImageSrcSet } from './heroImageUtils';

export interface HeroCTAProps extends BaseModuleProps {
  breadcrumbs?: BreadcrumbsProps;
  image?: ImageBlockProps;
}

export const HeroCTA: React.FC<HeroCTAProps> = ({
  breadcrumbs,
  anchorTag = undefined,
  container = CONTAINER_OPTIONS.CONTAINED,
  fillType = MODULE_FILL_TYPE_OPTIONS.BASE,
  gradientFillToken,
  layout = CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
  eyebrow,
  title,
  bodyRichTextAsHTML,
  alignment = HEADING_ALIGNMENT_OPTIONS.START,
  ctas,
  caption,
  image,
  isFirstModule,
}) => {
  const headingConfig: HeadingBlockProps = {
    eyebrow,
    title,
    titleSize: TITLE_SIZE_OPTIONS.LARGE,
    titleType: TITLE_TYPE_OPTIONS.H1,
    bodyRichTextAsHTML,
    alignment,
    ctas,
    caption,
    titleGradient: fillType === MODULE_FILL_TYPE_OPTIONS.IMAGE ? gradientFillToken : undefined,
  };

  const isImageFill = fillType === 'image';

  // Show breadcrumbs only if position is page-top
  const showBreadcrumbs =
    isFirstModule && breadcrumbs && breadcrumbs.position === BREADCRUMBS_POSITION_OPTIONS.PAGE_TOP;
  const breadcrumbStyles = showBreadcrumbs ? styles.with_breadcrumb : '';

  // Use the common module styles hook
  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container,
    fillType,
    gradientFillToken,
    layout,
    moduleClassName: 'module-hero-cta',
    styles,
    isFirstModule,
  });

  const sectionClassNames = classNames(
    sectionStyles,
    breadcrumbStyles,
    isImageFill && styles.hero_scrim,
    isImageFill && styles[`hero_scrim-${layout}`],
  );

  return (
    <section className={sectionClassNames} id={anchorTag}>
      {showBreadcrumbs && (
        <Breadcrumbs
          {...breadcrumbs}
          className={styles.module_breadcrumbs}
          showInverse={isImageFill}
        />
      )}
      {isImageFill && image && image.src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={image.src}
          srcSet={getHeroImageSrcSet(image.src, 'bg')}
          sizes="100vw"
          alt={image.alt || 'Hero image'}
          loading="eager"
          fetchPriority="high"
          className={styles.hero_image_fill}
        />
      )}
      <div className={classNames(moduleBaseStyles, styles.hero_container)}>
        <div className={classNames(styles.hero_content, styles[`hero_content-${layout}`])}>
          <HeadingBlock
            {...headingConfig}
            className={styles['hero-heading-block']}
            showInverse={isImageFill}
          />
          {!isImageFill && image && image.src && (
            <ImageBlock
              src={image.src}
              alt={image.alt}
              aspectRatio={image.aspectRatio || IMAGE_ASPECT_RATIO_OPTIONS.SQUARE}
              isLazyLoad={false}
              className={styles['module-content-swap']}
              srcSet={getHeroImageSrcSet(image.src, 'block')}
              sizes="(max-width: 824px) 100vw, 648px"
            />
          )}
          {isImageFill && image && image.src && (
            <div
              className={classNames(styles['module-content-swap'], styles['hero-filler-div'])}
            ></div>
          )}
        </div>
      </div>
    </section>
  );
};
